import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { StorageService } from './storage.service';
import { RoutineGroup, RoutineModel } from '../interfaces/routine';
import { Observable } from 'rxjs';
import { RoutineModelExercise } from '../interfaces/exercise';

@Injectable({
  providedIn: 'root',
})
export class RoutineService {
  USER_apiUrl = environment.apiUrl + '/user';
  ROUTINE_GROUP_URL = environment.apiUrl + '/routine-group';
  ROUTINE_MODEL_URL = environment.apiUrl + '/routine-model';
  WORKOUT_URL = environment.apiUrl + '/workout';
  ROUTINE_MODEL_TABLET_URL = environment.tabletUrl + '/routine-model';

  TABLET_API_URL = environment.tabletUrl + '/automatic-routine-model';

  constructor(
    private http: HttpClient,
    private storageService: StorageService
  ) {}

  // CRUD RoutineGroup

  createRoutineGroup(
    routineGroup: Pick<RoutineGroup, 'name' | 'weeks'>,
    userId: number,
    userExperience: number
  ): Observable<void> {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.post<void>(
      this.USER_apiUrl + '/' + userId + '/routine-group',
      {
        name: routineGroup.name,
        weeks: routineGroup.weeks,
        experienceLevel: userExperience,
      },
      { headers: httpHeaders }
    );
  }

  getRoutineGroups(userId: string): Observable<RoutineGroup[]> {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.get<RoutineGroup[]>(
      this.USER_apiUrl + '/' + userId + '/routine-group',
      { headers: httpHeaders }
    );
  }

  updateRoutineGroup(
    routineGroup: Pick<RoutineGroup, 'name' | 'id' | 'weeks'>,
    userId: number,
    userExperience: number
  ): Observable<void> {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.patch<void>(
      this.ROUTINE_GROUP_URL + '/' + routineGroup.id,
      {
        name: routineGroup.name,
        weeks: routineGroup.weeks,
        experienceLevel: userExperience,
      },
      { headers: httpHeaders }
    );
  }

  finishRoutineGroup(userId: number): Observable<void> {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.post<void>(
      this.USER_apiUrl + '/' + userId + '/routine-group/finish',
      {},
      { headers: httpHeaders }
    );
  }

  // CUD RoutineModel

  createRoutineModel(routineModel: RoutineModel): Observable<any> {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.post<any>(
      this.ROUTINE_GROUP_URL + '/routine',
      routineModel,
      { headers: httpHeaders }
    );
  }

  getRoutineModel(routineModelId: number): Observable<RoutineModel> {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.get<RoutineModel>(
      this.ROUTINE_MODEL_URL + '/' + routineModelId + '/last-workout',
      { headers: httpHeaders }
    );
  }

  getRoutineModelTablet(routineModelId: number): Observable<RoutineModel> {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.get<RoutineModel>(
      this.ROUTINE_MODEL_TABLET_URL + '/' + routineModelId,
      { headers: httpHeaders }
    );
  }

  updateRoutineModel(routineModel: RoutineModel): Observable<void> {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.patch<void>(
      `${this.ROUTINE_MODEL_URL}/${routineModel.id}`,
      routineModel,
      { headers: httpHeaders }
    );
  }

  deleteRoutineModel(routineModelId: number): Observable<void> {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.delete<void>(
      `${this.ROUTINE_MODEL_URL}/${routineModelId}`,
      { headers: httpHeaders }
    );
  }

  // CD RoutineModelExercise

  createExerciseInRoutineModel(
    exercise: RoutineModelExercise
  ): Observable<void> {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.post<void>(
      this.ROUTINE_MODEL_URL + '/exercise',
      exercise,
      { headers: httpHeaders }
    );
  }

  deleteExercise(exerciseId: number, edit: boolean = false): Observable<void> {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.delete<void>(
      `${this.ROUTINE_MODEL_URL}/exercise/${exerciseId}${
        edit ? '?put=true' : ''
      }`,
      { headers: httpHeaders }
    );
  }

  switchExerciseOrder(
    routineModelId: number,
    exerciseGroupIDA: number,
    exerciseGroupIDB: number
  ): Observable<void> {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.patch<void>(
      this.ROUTINE_MODEL_URL + '/' + routineModelId + '/switch/exercise-group',
      { exerciseGroupIDA, exerciseGroupIDB },
      { headers: httpHeaders }
    );
  }

  getProgretion(userId: number, routineGroupId: number) {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.post<void>(
      `${this.USER_apiUrl}/${userId}/routine-group/${routineGroupId}/progretion`,
      {},
      { headers: httpHeaders }
    );
  }

  importRoutineModel(routineGroupId: number, routineModelId: number) {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.post<void>(
      `${this.ROUTINE_GROUP_URL}/${routineGroupId}/import/routine-model/${routineModelId}`,
      {},
      { headers: httpHeaders }
    );
  }

  // TABLET

  createAutomaticRoutine(automaticRoutineData: any) {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.post<void>(
      `${this.TABLET_API_URL}`,
      { ...automaticRoutineData },
      {
        headers: httpHeaders,
      }
    );
  }

  getAllAutomaticRoutines() {
    // const token = this.storageService.get('accessToken', false);
    // const httpHeaders = new HttpHeaders().set('Access-Token', token);

    // return this.http.get<void>(`${this.TABLET_API_URL}`, {
    //   headers: httpHeaders,
    // });

    const observable = new Observable();
    const data = [
      {
        id: 1,
        name: 'Laureano',
        createdAt: '',
      },
      {
        id: 2,
        name: 'Federico',
        createdAt: '',
      },
    ];

    // observable.next()
    return;
  }

  getAutomaticRoutineDetail(routineModelId: number) {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.get<void>(`${this.TABLET_API_URL}/${routineModelId}`, {
      headers: httpHeaders,
    });
  }

  getRoutineGroupCalendar(routineGroupId: number) {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.get<void>(
      `${this.ROUTINE_GROUP_URL}/${routineGroupId}/workouts`,
      {
        headers: httpHeaders,
      }
    );
  }

  getWorkoutDetail(workoutId: number) {
    const token = this.storageService.get('accessToken', false);
    const httpHeaders = new HttpHeaders().set('Access-Token', token);

    return this.http.get<void>(`${this.WORKOUT_URL}/${workoutId}`, {
      headers: httpHeaders,
    });
  }
}
